/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import Text from "../ui/Text";
import FormatPrice from "../formatters/FormatPrice";
import FormatDateTime from "../formatters/FormatDateTime";
import { useTranslate } from "../../hooks/useTranslate";
import { getRecurringMembershipsForDisplay, formatRecurringPeriod } from "../../utils/helpers/membership";
import Icon from "../ui/Icon";
import Flex from "../ui/Flex";
import Stack from "../ui/Stack";
import TextCircleDivider from "../ui/TextCircleDivider";

const Block = styled.div`
  display: grid;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 1rem;
  background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.05);
`;

const StyledIcon = styled(Icon.RecurringCharge)`
  fill: none;
  color: rgba(${({ theme }) => theme.colors.accent.rgba});
`;

const Row = styled(Flex)`
  min-width: 0;

  &:not(:last-child) {
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  }

  ${({ theme }) => theme.containerMedia.sm} {
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
  }
`;

const Metadata = styled(Flex)`
  overflow: hidden;
  white-space: nowrap;
`;

const Price = styled(Text)`
  flex-shrink: 0;
`;

const RecurringChargesBlock = ({ invoice, optedIn = false }) => {
  const { _t } = useTranslate();
  const memberships = getRecurringMembershipsForDisplay(invoice, optedIn);

  if (memberships.length === 0) return null;

  return (
    <Stack $gap="0.5rem">
      <Block>
        <Flex $direction="row" $align="center" $gap="0.5rem">
          <StyledIcon />
          <Text weight="bold" color="dark">Recurring charges</Text>
        </Flex>

        <Stack $gap="0.5rem">
          {memberships.map((membership) => (
            <Row key={membership.id} $direction="row" $align="baseline" $justify="space-between" $gap="1rem">
              <Metadata $direction="row" $align="center" $gap="0.25rem">
                <Text color="dark" lineClamp={1}>
                  {membership.name}
                </Text>
                <TextCircleDivider color="accent" size={3} />
                {membership.nextDate && (
                  <Text color="accent" size="small">
                    {_t("next")}{" "}
                    <FormatDateTime value={membership.nextDate} format={FormatDateTime.FORMAT.DATE} />
                  </Text>
                )}
              </Metadata>
              <Price color="dark" weight="bold">
                <FormatPrice value={membership.recurringPrice} currency={membership.currency} />/
                <Text color="dark" size="small" as="span">{formatRecurringPeriod(membership.duration, membership.durationType, _t)}</Text>
              </Price>
            </Row>
          ))}
        </Stack>
      </Block>
      <Text size="small">
        Each membership renews automatically on its own schedule until cancelled. You can cancel anytime in your profile.
      </Text>
    </Stack>
  );
};

export default RecurringChargesBlock;
